import React from 'react';
import { toastError, toastInfo } from '../utils/toast';

type GlobalErrorBoundaryProps = {
  children: React.ReactNode;
};

type GlobalErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
};

export class GlobalErrorBoundary extends React.Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidMount(): void {
    // Captura global de erros não capturados em runtime
    window.addEventListener('error', this.handleWindowError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount(): void {
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    // Garantir que o scroll seja liberado ao desmontar
    document.body.style.overflow = 'unset';
  }

  componentDidUpdate(prevProps: GlobalErrorBoundaryProps, prevState: GlobalErrorBoundaryState) {
    // Bloquear scroll quando erro for exibido
    if (this.state.hasError && !prevState.hasError) {
      document.body.style.overflow = 'hidden';
    } else if (!this.state.hasError && prevState.hasError) {
      document.body.style.overflow = 'unset';
    }
  }

  private handleWindowError = (event: ErrorEvent) => {
    const error = event.error || new Error(event.message);
    // Atualiza estado para acionar UI de fallback
    this.setState({ hasError: true, error });
    // Tenta notificar o usuário
    toastError({
      title: 'Erro de execução',
      message: `${event.message}`,
      position: 'center',
      class: 'izi-center-force error-style',
      timeout: 8000,
    });
    // Log detalhado
    console.error('[GlobalErrorBoundary] window.onerror capturado:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error,
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    this.setState({ hasError: true, error: reason });
    toastError({
      title: 'Erro não tratado',
      message: reason.message || 'Falha inesperada em promessa',
      position: 'center',
      class: 'izi-center-force error-style',
      timeout: 8000,
    });
    console.error('[GlobalErrorBoundary] unhandledrejection capturado:', reason);
  };

  static getDerivedStateFromError(error: Error): GlobalErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Guardar detalhes para possível relatório
    this.setState({ error, errorInfo });

    // Notificar usuário de que ocorreu um erro inesperado
    toastError({
      title: 'Erro inesperado',
      message: 'Ocorreu um erro na aplicação. Estamos registrando detalhes para análise.',
      position: 'center',
      class: 'izi-center-force error-style',
      timeout: 8000,
    });

    // Log detalhado para depuração
    console.error('[GlobalErrorBoundary] Erro capturado:', error);
    console.error('[GlobalErrorBoundary] Detalhes:', errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleCopyDetails = () => {
    const { error, errorInfo } = this.state;
    const details = [
      'Erro inesperado capturado pelo GlobalErrorBoundary:',
      error ? `${error.name}: ${error.message}` : 'Sem detalhes de erro',
      errorInfo ? `Stack:
${errorInfo.componentStack}` : 'Sem stack de componente',
    ].join('\n\n');
    navigator.clipboard.writeText(details).catch(() => {
      // Fallback simples
      const textarea = document.createElement('textarea');
      textarea.value = details;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
    toastInfo({
      title: 'Detalhes copiados',
      message: 'Informações do erro foram copiadas para a área de transferência.',
      position: 'center',
      class: 'izi-center-force info-style',
      timeout: 5000,
    });
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Problema detectado</h2>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-gray-700 dark:text-gray-300">
                Ocorreu um erro e a interface não pôde ser renderizada corretamente.
              </p>
              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 text-red-800 text-sm">
                  <strong>{error.name}</strong>: {error.message}
                </div>
              )}
              <div className="flex gap-2 justify-end mt-2">
                <button
                  onClick={this.handleCopyDetails}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Copiar detalhes
                </button>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Recarregar página
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}